import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu';
import { IconCopy } from '~shared/components/icons/standard';
import { useDuplicateWorkspaceQuizDialog } from '~teacher/components/workspaces/DuplicateWorkspaceQuizDialog';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';
import { WorkspaceQuiz } from '../../../../../api/workspaces/quiz/types';

interface UseDuplicateQuizActionParam {
  workspaceId: string;
  paywallCallback: () => void;
}

export function useDuplicateQuizAction({
  workspaceId,
  paywallCallback,
}: UseDuplicateQuizActionParam) {
  const {
    permissions: { duplicateQuiz: canDuplicateQuiz },
    roleQuery,
  } = useWorkspacePermissionsV2({ workspaceId: workspaceId });
  const [duplicateWorkspaceQuizDialogState, dispatchCopyDialog] =
    useDuplicateWorkspaceQuizDialog();
  const duplicateQuizAction = React.useMemo(
    () =>
      ({
        key: 'duplicate',
        title: translate('Duplicate'),
        icon: <IconCopy />,
        action: ({ data, close }) => {
          const firstItem = data[0];
          if (firstItem.type === 'quiz') {
            const { id, workspaceId, name } = firstItem as WorkspaceQuiz;
            dispatchCopyDialog({
              type: 'open',
              payload: {
                workspaceQuizId: id,
                workspaceId: workspaceId,
                initialQuizName: name,
              },
            });
            close();
          }
        },
        constraint: ({ data: quizzes }: { data: WorkspaceItem[] }) => {
          if (roleQuery.data === WorkspaceRole.Viewer) return false;

          return quizzes.every((q) => q.type === 'quiz');
        },
        showProBadge: !canDuplicateQuiz,
        disabled: ({ data: quizzes }) =>
          canDuplicateQuiz === false || quizzes.length > 1,
        paywallCallback: !canDuplicateQuiz ? paywallCallback : null,
      } as Action<WorkspaceItem>),
    [canDuplicateQuiz, paywallCallback, dispatchCopyDialog, roleQuery.data]
  );

  return {
    duplicateWorkspaceQuizDialogState,
    dispatchCopyDialog,
    duplicateQuizAction,
  };
}
